import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { listQuerySchema } from './pagination';
import { JOB_STATUS_VALUES, type JobStatusValue } from './job';

/*
 * What the employee portal sends and what it gets back. ROADMAP.md §6,
 * reference/business-rules.md §2.
 *
 * **These views are built, never borrowed** — the same rule as
 * `client-portal.ts`, and it has two edges here rather than one.
 *
 *   - **Inward, about the office.** A cleaner does not read the internal
 *     cancellation trail, the status-history timeline, `client_note` or
 *     `satisfaction_rating`. None of those columns are read by the service
 *     behind this file.
 *   - **Sideways, about a colleague.** `toEmployeeView` in employee-service
 *     exists precisely so a pay rate and a home address cannot leak between
 *     people who work together. Crew members appear here as a name and a role
 *     label, and nothing else.
 *
 * The one place this file is *more* generous than the client's is
 * `accessNotes`: a lockbox code is how the cleaner gets in the front door. It
 * is on the job detail, for a job they are assigned to, and it is deliberately
 * absent from the client lookup in 6.7 — business-rules.md §2.
 */

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/**
 * A colleague on the same job.
 *
 * The client portal already renders crew by full name (question 11), so a
 * cleaner seeing the same name tells them nothing the customer does not
 * already know.
 */
export interface EmployeeCrewMember {
  employeeId: string;
  name: string;
  /** "Lead cleaner" or "Cleaner". Humanised here, never the stored role. */
  roleLabel: string;
  /** Whether this is the person asking — the card says "you", not their name. */
  isMe: boolean;
}

/**
 * Photo-gated progress, and it is the *headline* number on this portal rather
 * than a detail. business-rules.md §3.1: where a task requires a photo,
 * uploading the first one is the completion event, so "how far through am I"
 * and "how many photos have I taken" are the same question.
 *
 * Both counts are computed from the job's tasks server-side. Nothing in the
 * browser derives progress from anything else — that is what makes the count
 * on a list card and the banner on the checklist incapable of disagreeing.
 */
export interface EmployeeTaskProgress {
  /** Tasks that must be done before the job can be completed. */
  requiredTotal: number;
  requiredCompleted: number;
  /** Of the required ones, how many are waiting on a photo specifically. */
  photoRequiredTotal: number;
  photoRequiredCompleted: number;
}

export interface EmployeeJobListItem {
  id: string;
  jobNumber: string;

  status: JobStatusValue;
  /** The display value. A raw `in_progress` must never reach a screen (P5). */
  statusLabel: string;

  clientName: string;
  addressSnapshot: string;
  propertyNickname: string | null;

  /** "Deep Cleaning", never `deep_cleaning` — P25 spans this portal. */
  serviceTypeName: string;
  categoryName: string | null;

  scheduledDate: string;
  scheduledTime: string;
  estimatedDurationMinutes: number;

  isHighPriority: boolean;

  progress: EmployeeTaskProgress;
  crew: EmployeeCrewMember[];
}

export const employeeJobListQuerySchema = listQuerySchema.extend({
  status: z.enum([...JOB_STATUS_VALUES, 'all']).default('all'),

  /** A bounded window, for the schedule week view — 6.6. */
  from: calendarDateSchema.optional(),
  to: calendarDateSchema.optional(),
});

export type EmployeeJobListQuery = z.infer<typeof employeeJobListQuerySchema>;

// ---------------------------------------------------------------------------
// Job detail — the checklist screen
// ---------------------------------------------------------------------------

/**
 * One line of the checklist.
 *
 * **There is no `isCompleted` a cleaner can set.** Where `requiresPhoto` is
 * true — which is the default — `isCompleted` was written by the photo landing,
 * because uploading the first photo *is* the completion event
 * (business-rules.md §3.1). The screen therefore has no checkbox; it has an Add
 * photo control and the caption "Upload a photo to mark this task complete".
 *
 * `photoCount` is how many photos are on the task, which is not the same number
 * as "completed": a second photo on a done task does not complete anything
 * twice. Progress is `requiredCompleted`, never `photos.length`.
 */
export interface EmployeeJobTaskView {
  id: string;
  name: string;
  sortOrder: number;
  isRequired: boolean;
  requiresPhoto: boolean;
  isCompleted: boolean;
  completedAt: string | null;
  /** Who took the photo that completed it, when somebody did. */
  completedByName: string | null;
  photoCount: number;
}

export interface EmployeeJobPhotoView {
  id: string;
  jobTaskId: string | null;
  /** "Before", "After", "Issue" — humanised, never the stored value. */
  kindLabel: string;
  caption: string | null;
  uploadedByName: string;
  uploadedAt: string;
  /** The employee-scoped app URL. The stored key never leaves the server. */
  url: string;
}

/** The address as the crew needs it, plus what they need to get in. */
export interface EmployeeJobPropertyView {
  nickname: string | null;
  fullAddress: string;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  categoryName: string | null;
  specialInstructions: string | null;

  /**
   * **Lockbox and gate codes.** business-rules.md §2 gives an employee these
   * "for a property on a job they are assigned to" — which is exactly and only
   * where this type is served from. The client lookup in 6.7 does not carry
   * them, and that asymmetry is the whole point of the rule.
   */
  accessNotes: string | null;
}

/**
 * What somebody needs to know before they start work in this house.
 *
 * **Added 2026-08-06 with the allergy field, and it fixed something older than
 * that.** `pet_notes` had existed since phase 3 and had never once been served
 * to a cleaner — it sat on the admin's Preferences tab, where the person who
 * needed it was not looking. A field about a dog behind a door is worth nothing
 * to the office and everything to whoever opens the door.
 *
 * **Same scope as `accessNotes`, deliberately**: served only for a job the
 * cleaner is assigned to, never from the client lookup (business-rules.md §2).
 * These are facts about a household, and browsing them for a stranger's address
 * is exactly what that rule exists to prevent.
 *
 * Both null is normal and renders as nothing at all — an empty "Household" card
 * saying "no allergies recorded" would read as *checked and clear*, which is a
 * different claim from *nobody has written anything down*.
 */
export interface EmployeeJobHouseholdView {
  petNotes: string | null;
  allergyNotes: string | null;
}

export interface EmployeeJobDetail extends EmployeeJobListItem {
  /** What the office wrote about this visit specifically. */
  specialInstructions: string | null;

  /** Why the office called it off, when they did. */
  cancelledReason: string | null;

  startedAt: string | null;
  completedAt: string | null;

  property: EmployeeJobPropertyView | null;

  /** Pets and allergies. Null when the office has recorded neither. */
  household: EmployeeJobHouseholdView | null;

  tasks: EmployeeJobTaskView[];
  photos: EmployeeJobPhotoView[];

  /**
   * What this cleaner may do to this job right now, decided on the server.
   *
   * Not a convenience: the browser must not be the thing that knows the
   * lifecycle. `jobService` refuses an illegal transition whatever these say,
   * and these exist so the screen does not offer a button that is going to be
   * refused. Two different questions with the same answer, and the server owns
   * both.
   */
  canStart: boolean;
  canComplete: boolean;
  /** Photos are accepted even after completion — see `recordPhotoUpload`. */
  canUploadPhotos: boolean;

  /**
   * There is no status-history timeline here. That is the office's audit trail,
   * and the client portal leaves it out for the same reason (5.2).
   */
}

/**
 * The fields beside the file on an employee's upload.
 *
 * **No `uploadedByEmployeeId`**, unlike the admin's `uploadPhotoSchema`. The
 * office attaches photos on a cleaner's behalf and has to say whose evidence it
 * is; a cleaner uploading their own photo is already identified by their
 * session, and a field for it would be a field somebody could put another
 * person's id in.
 */
export const employeeUploadPhotoSchema = z.object({
  jobTaskId: z.uuid().optional(),
  kind: z.enum(['before', 'after', 'issue']).default('after'),
  caption: z
    .string()
    .trim()
    .max(512, 'Must be 512 characters or fewer')
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),
});

export type EmployeeUploadPhotoInput = z.infer<typeof employeeUploadPhotoSchema>;

// ---------------------------------------------------------------------------
// Timekeeping — ROADMAP.md §6.5
// ---------------------------------------------------------------------------

/**
 * One shift. **Not a job** — one clock-in covers several.
 *
 * `jobId` is a hint about what the person was doing, not a link the job
 * lifecycle depends on. Nothing in timekeeping writes `job.status`
 * (FINDINGS.md §11.2).
 */
export interface ShiftView {
  id: string;
  clockInAt: string;
  /** Null means still on the clock. */
  clockOutAt: string | null;
  /** Computed server-side on clock-out. Null while the shift is open. */
  durationMinutes: number | null;
  jobId: string | null;
  jobNumber: string | null;
  /** True once an admin has corrected it (10.3) — the screen says so. */
  wasCorrected: boolean;
}

export interface OpenShift extends ShiftView {
  /**
   * The server's clock at the moment this was read.
   *
   * The timer on the dashboard counts from `clockInAt` towards this rather than
   * towards `Date.now()`, so a phone whose clock is ten minutes fast does not
   * show a shift ten minutes longer than payroll will pay for. The elapsed
   * figure a cleaner reads and the figure on the timesheet have to be the same
   * figure.
   */
  serverNow: string;
}

export interface TimeSummary {
  /** Closed shifts only — an open one has no duration yet. */
  minutes: number;
  shifts: number;
}

/**
 * Clocking in may name the job being started, and may not.
 *
 * Optional because a shift covering three cleans belongs to none of them. When
 * it is given, the server still checks the employee is assigned to it — a shift
 * cannot be attributed to somebody else's job.
 */
export const clockInSchema = z.object({
  jobId: z.uuid().nullable().optional(),
});

export type ClockInInput = z.infer<typeof clockInSchema>;

/** What the dashboard's Time Tracking card needs, in one response. */
export interface TimeTrackingCard {
  /** Null when they are not on the clock. */
  openShift: OpenShift | null;
  /** Hours clocked today, closed shifts only. */
  today: TimeSummary;
  /** The business-zone day `today` covers. */
  date: string;
}

// ---------------------------------------------------------------------------
// My schedule — ROADMAP.md §6.6
// ---------------------------------------------------------------------------

/**
 * A bounded window, not a paginated list. The same shape as the admin
 * calendar's read and for the same reason (P8): the *visible range* is the
 * limit, so a screen asks for exactly what it can show.
 */
export const employeeScheduleQuerySchema = z.object({
  from: calendarDateSchema,
  to: calendarDateSchema,
});

export type EmployeeScheduleQuery = z.infer<typeof employeeScheduleQuerySchema>;

/**
 * One day of the window, with **both** figures on it.
 *
 * "Scheduled 8h, clocked 7h 45m" is more use than either number alone, and it
 * is the pair the prototype's per-day row shows. The scheduled half arrived with
 * `employee_weekly_schedule` in 7.1; before that this row carried only the
 * clocked figure and the screen said so.
 *
 * `explanation` is the resolver's own sentence — "Tuesday is a day off on their
 * weekly schedule", "Marked unavailable on 14 Aug 2026 — Vacation". It is what
 * turns a day with no hours from a blank into an answer, and it is the same
 * sentence the assignment screen shows the office (business-rules.md §4).
 */
export interface EmployeeScheduleDay {
  date: string;
  /** After override → weekly → unavailable. Unavailable is the default. */
  isAvailable: boolean;
  startTime: string | null;
  endTime: string | null;
  /** Unpaid, and already taken out of `minutesScheduled`. */
  breakMinutes: number;
  /** Span minus break. `0` on a day off — zero is `0`, never a dash (P1). */
  minutesScheduled: number;
  /** Closed shifts that began on this day, in the business's zone. */
  minutesClocked: number;
  explanation: string;
}

/**
 * The week, as data. ROADMAP.md §6.6, completed by §7.1.
 *
 * **Two hour figures, and they mean different things.** `minutesScheduled` is
 * what the office has this person down for — `employee_weekly_schedule` and its
 * overrides, resolved by `availabilityService`. `minutesClocked` is what
 * `time_log` recorded. The screen shows both and names both, because a week
 * where they disagree is exactly the week somebody needs to look at.
 */
export interface EmployeeSchedule {
  from: string;
  to: string;

  jobs: EmployeeJobListItem[];
  shifts: ShiftView[];

  /** Every day in the window, in order — scheduled and clocked side by side. */
  days: EmployeeScheduleDay[];

  /** Zero is `0` on every one of these (P1). */
  counts: {
    /** Days in the window with a job on them or a shift clocked. */
    daysWorked: number;
    jobs: number;
    completed: number;
  };

  /** Total clocked in the window, closed shifts only. */
  minutesClocked: number;

  /** Total the office has them down for across the window. */
  minutesScheduled: number;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/**
 * The landing screen, in one response.
 *
 * One call rather than three, for the client dashboard's reason: the counts and
 * the list answer the same question ("what does my day look like"), and two
 * requests arriving a second apart can disagree with each other on screen.
 */
export interface EmployeeDashboard {
  /** First name, for the greeting. */
  firstName: string;

  /**
   * The calendar day these counts are about, in the business's zone — never the
   * browser's idea of today. A cleaner opening this at 7pm is still on today's
   * jobs; `new Date().toISOString()` would have moved them to tomorrow's.
   */
  date: string;

  /** Zero is `0` on both of these, never a dash (P1, P10). */
  counts: {
    jobsToday: number;
    completedToday: number;
  };

  /** Today's jobs, in time order — the prototype's ordering and the right one. */
  todaysJobs: EmployeeJobListItem[];

  /**
   * The Time Tracking card, which **leads this page** — FINDINGS.md §11.5 is
   * right that clocking in is the first thing a cleaner does, so it is the first
   * thing on the screen.
   */
  time: TimeTrackingCard;
}

// ---------------------------------------------------------------------------
// Shared display helpers
// ---------------------------------------------------------------------------

/**
 * Never render the stored role. design-system.md §7, defect P5.
 *
 * Deliberately the same words as the client portal's `CREW_ROLE_LABELS`: the
 * customer and the cleaner reading the same job should see the same job.
 */
export const EMPLOYEE_CREW_ROLE_LABELS: Record<'lead' | 'cleaner' | 'support', string> = {
  lead: 'Lead cleaner',
  cleaner: 'Cleaner',
  support: 'Support',
};

// ---------------------------------------------------------------------------
// My profile — ROADMAP.md §6.7
// ---------------------------------------------------------------------------

/**
 * A cleaner's own record, as they see it.
 *
 * **The asymmetry with the client portal is deliberate**, and it is the
 * prototype's: an employee edits their own name, phone and home address; a
 * client edits nothing (question 10). The reason is that these are different
 * kinds of fact. A client's service address is where a cleaner is being sent —
 * changing it under a booked job sends somebody to an empty house. An
 * employee's home address is their own detail, used for travel planning, and
 * nobody is dispatched to it.
 *
 * Three things on this screen are **not** editable and each says why rather
 * than being hidden (design-system.md §7):
 *
 *   - **The login.** "Cannot change", straight from the prototype and good
 *     posture: an identity that moves is one audit rows and password resets
 *     stop agreeing about.
 *   - **Skills and max jobs per day.** Admin-managed, labelled so — FINDINGS.md
 *     §11.5 calls this out as the right way to show a read-only field, because
 *     it says who to ask.
 *   - **The pay rate**, which is not here at all. An employee may see their own
 *     (business-rules.md §2) and this screen does not read the column; the
 *     office answers pay questions.
 */
export interface EmployeeProfileView {
  firstName: string;
  lastName: string;
  fullName: string;

  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;

  /** The login. One of these two is always present; neither can be changed. */
  loginEmail: string | null;
  username: string | null;

  /** Where a password reset goes when the login is a username (P9). */
  recoveryEmail: string | null;

  // Admin-managed, shown read-only.
  positionName: string | null;
  maxJobsPerDay: number;
  skills: { id: string; name: string }[];
  hireDate: string | null;
}

const trimmedOptional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${String(max)} characters or fewer`)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional();

/**
 * What a cleaner may change about themselves.
 *
 * **Deliberately not `updateEmployeeSchema`.** That one carries
 * `maxJobsPerDay`, `hourlyRateCents`, `positionId` and `skillIds` — every field
 * the admin owns. Reusing it and stripping the extras in the handler would put
 * the rule in the handler, and the handler that forgets is the one that ships.
 * A separate, shorter schema cannot carry a rate at all.
 */
export const employeeProfileUpdateSchema = z.object({
  firstName: z.string().trim().min(1, 'Required').max(128),
  lastName: z.string().trim().min(1, 'Required').max(128),
  phone: trimmedOptional(32),
  address: trimmedOptional(255),
  city: trimmedOptional(128),
  state: trimmedOptional(64),
  zip: trimmedOptional(16),
});

export type EmployeeProfileUpdateInput = z.infer<typeof employeeProfileUpdateSchema>;

/**
 * The three types an employee is offered — data-model.md §8, role-scoped.
 *
 * A different set from the client's, which is the point: notification types are
 * per role, and the preference screen only offers the ones that apply.
 * `stale_clock_in` is an *admin* alert about a forgotten clock-out (7.6) and is
 * not here.
 */
export const EMPLOYEE_NOTIFICATION_TYPES = [
  'job_assigned',
  'job_cancelled',
  'schedule_updated',
  'job_reminder',
] as const;

export type EmployeeNotificationTypeValue = (typeof EMPLOYEE_NOTIFICATION_TYPES)[number];

export const EMPLOYEE_NOTIFICATION_TYPE_LABELS: Record<EmployeeNotificationTypeValue, string> = {
  job_assigned: 'Job Assigned',
  job_cancelled: 'Job Cancelled',
  schedule_updated: 'Schedule Updated',
  job_reminder: 'Job Reminder',
};

/** What each one means, in the crew's language. */
export const EMPLOYEE_NOTIFICATION_TYPE_HINTS: Record<EmployeeNotificationTypeValue, string> = {
  job_assigned: 'When the office puts you on a job.',
  job_cancelled: 'When a job you are on is called off.',
  schedule_updated: 'When one of your jobs is moved to a different day or time.',
  job_reminder: 'The evening before, listing tomorrow’s jobs.',
};

export const employeeNotificationPreferenceSchema = z.object({
  type: z.enum(EMPLOYEE_NOTIFICATION_TYPES),
  channel: z.enum(['in_app', 'email', 'push']),
  isEnabled: z.boolean(),
});

export type EmployeeNotificationPreferenceInput = z.infer<
  typeof employeeNotificationPreferenceSchema
>;

export interface EmployeeNotificationPreferences {
  /** Keyed `type:channel`. **Absent means on** — see the migration. */
  disabled: string[];
}

// ---------------------------------------------------------------------------
// Client lookup — ROADMAP.md §6.7, open question Q5
// ---------------------------------------------------------------------------

/**
 * A customer, as a cleaner sees them.
 *
 * **Q5's default is to match the prototype: employees see every client and may
 * add notes**, including clients they have never worked for. The crew may rely
 * on it — ringing ahead, checking a name — and taking it away would be a change
 * the business did not ask for.
 *
 * **What is not here is where the written rule wins over the prototype.**
 * business-rules.md §2 gives an employee properties "read, assigned only", so
 * this view carries no property list and no addresses: a cleaner sees an address
 * through a *job* they were sent to, and `address_snapshot` on that job is
 * already theirs. `accessNotes` — lockbox and gate codes — is doubly absent, and
 * that is the line the brief for this phase draws in bold.
 *
 * The practical effect: a cleaner can look up any customer's name and phone
 * number, and can see the addresses of houses they have actually been sent to.
 * They cannot browse the address of a customer they have never worked for. That
 * narrowing is flagged for the owner in PROGRESS.md.
 */
export interface EmployeeClientListItem {
  id: string;
  /** Business name when there is one, otherwise the person. Never a slug. */
  displayName: string;
  contactName: string;
  phone: string | null;
  email: string | null;

  /**
   * Jobs **this employee** has done for them. The prototype shows this figure
   * and it is the useful one — "have I been here before".
   */
  myJobCount: number;
}

export interface EmployeeClientNote {
  id: string;
  body: string;
  authorName: string | null;
  createdAt: string;
}

/**
 * One customer, with the history this employee has with them.
 *
 * **The count and the list come back together, from the same query.** That is
 * P22 designed out rather than fixed: the prototype's screen renders "No jobs
 * found for this client" beside a header reading "2 Total Jobs", because the
 * count and the list were two requests and one of them 500'd. Here there is one
 * request, and if it fails the screen shows an error rather than an empty list.
 */
export interface EmployeeClientDetail extends EmployeeClientListItem {
  /** Every job this employee was on for this client, soonest first. */
  myJobs: EmployeeJobListItem[];
  notes: EmployeeClientNote[];
}

export const employeeClientNoteSchema = z.object({
  body: z.string().trim().min(1, 'Write the note first').max(5000),
});

export type EmployeeClientNoteInput = z.infer<typeof employeeClientNoteSchema>;
